// Plan hierarchy for access control
const PLAN_HIERARCHY = {
  "BASIC": 1,
  "VECTOR PRO": 2,
  "EXPERT": 3,
  "EXPERT_TRIAL": 3  // Same level as EXPERT for access control
};

/**
 * Get the effective plan for a user, considering trial status.
 * This mirrors the backend get_effective_plan function.
 * @param {string} userPlan - User's current subscription plan
 * @returns {string} - Effective plan name
 */
export const getEffectivePlan = (userPlan) => {
  if (userPlan === "EXPERT_TRIAL") {
    return "EXPERT_TRIAL";
  }
  return userPlan || "BASIC";
};

/**
 * Check if user's plan has access to required plan level
 * @param {string} userPlan - User's current subscription plan
 * @param {string} requiredPlan - Required plan level for access
 * @returns {boolean} - True if user has access, false otherwise
 */
export const hasPlanAccess = (userPlan, requiredPlan) => {
  if (!userPlan || !requiredPlan) return false;
  
  // Get effective plan (handles trial logic)
  const effectivePlan = getEffectivePlan(userPlan);
  
  return PLAN_HIERARCHY[effectivePlan] >= PLAN_HIERARCHY[requiredPlan];
};

/**
 * Get available models for a user's plan
 * @param {string} userPlan - User's current subscription plan
 * @returns {Object} - Object with available models and their access status
 */
export const getAvailableModels = (userPlan) => {
  return {
    long_term: {
      name: "Long-term Investment Model",
      description: "Predicts long-term stock performance",
      required_plan: "BASIC",
      available: hasPlanAccess(userPlan, "BASIC"),
      icon: "📈",
      path: "/long-term-model"
    },
    short_term: {
      name: "Short-term Investment Model", 
      description: "Predicts short-term stock movements",
      required_plan: "VECTOR PRO",
      available: hasPlanAccess(userPlan, "VECTOR PRO"),
      icon: "📊",
      path: "/short-term-model"
    },
    swing: {
      name: "Swing Trading Model",
      description: "Advanced swing trading predictions", 
      required_plan: "EXPERT",
      available: hasPlanAccess(userPlan, "EXPERT") || hasPlanAccess(userPlan, "EXPERT_TRIAL"),
      icon: "🎯",
      path: "/swing-model"
    },
    ai_analyst: {
      name: "AI Stock Analyst",
      description: "Natural language stock analysis with AI",
      required_plan: "EXPERT",
      available: hasPlanAccess(userPlan, "EXPERT") || hasPlanAccess(userPlan, "EXPERT_TRIAL"),
      icon: "🤖",
      path: "/ai-analyst"
    }
  };
};

/**
 * Get plan features and limits
 * @param {string} userPlan - User's current subscription plan
 * @returns {Object} - Plan features and limits
 */
export const getPlanFeatures = (userPlan) => {
  const planFeatures = {
    "BASIC": {
      daily_searches: 1,
      watchlist_items: 5,
      price_alerts: 3,
      models: ["long_term"],
      description: "Perfect for beginners"
    },
    "VECTOR PRO": {
      daily_searches: 25,
      watchlist_items: 25,
      price_alerts: 15,
      models: ["long_term", "short_term"],
      description: "For active investors"
    },
    "EXPERT": {
      daily_searches: 50,
      watchlist_items: -1, // Unlimited
      price_alerts: -1, // Unlimited
      models: ["long_term", "short_term", "swing", "ai_analyst"],
      description: "Complete trading solution"
    },
    "EXPERT_TRIAL": {
      daily_searches: 50,
      watchlist_items: -1, // Unlimited
      price_alerts: -1, // Unlimited
      models: ["long_term", "short_term", "swing", "ai_analyst"],
      description: "Complete trading solution (7-Day Free Trial)"
    }
  };

  return planFeatures[userPlan] || planFeatures["BASIC"];
};

/**
 * Format plan name for display
 * @param {string} planName - Raw plan name
 * @returns {string} - Formatted plan name
 */
export const formatPlanName = (planName) => {
  if (!planName) return "No Plan";
  
  const planNames = {
    "BASIC": "Basic Plan",
    "VECTOR PRO": "Vector Pro Plan", 
    "EXPERT": "Expert Plan",
    "EXPERT_TRIAL": "Expert Plan (Free Trial)"
  };
  
  return planNames[planName] || planName;
};

/**
 * Get plan badge color
 * @param {string} planName - Plan name
 * @returns {string} - Hex color code
 */
export const getPlanBadgeColor = (planName) => {
  const colors = {
    "BASIC": "#04a7a7",
    "VECTOR PRO": "#1d3557", 
    "EXPERT": "#f46036",
    "EXPERT_TRIAL": "#f46036"  // Same color as EXPERT
  };
  
  return colors[planName] || "#6b7280";
};

/**
 * Get plan limits for display
 * @param {string} userPlan - User's current subscription plan
 * @returns {Object} - Plan limits for display
 */
export const getPlanLimits = (userPlan) => {
  const planFeatures = getPlanFeatures(userPlan);
  
  return {
    watchlistItems: planFeatures.watchlist_items === -1 ? 'Unlimited' : planFeatures.watchlist_items,
    priceAlerts: planFeatures.price_alerts === -1 ? 'Unlimited' : planFeatures.price_alerts,
    dailySearches: planFeatures.daily_searches === -1 ? 'Unlimited' : planFeatures.daily_searches,
    isUnlimitedWatchlist: planFeatures.watchlist_items === -1,
    isUnlimitedAlerts: planFeatures.price_alerts === -1,
    isUnlimitedSearches: planFeatures.daily_searches === -1
  };
};
